// app/(routes)/conversations/page.tsx
'use client';

import { AlertTriangle, ArrowRight, Building2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { useAuthHook } from '@/app/auth/services_and_hooks/useAuthHook';
import { HTFBaseCard } from '@/app/shared/components/cards/HTFBaseCard';
import { HTFSectionHeader } from '@/app/shared/components/layout/HTFSectionHeader';
import { LoadingIndicator } from '@/components/ui/loading-indicator';

export default function OrganizationsListPage() {
  const router = useRouter();
  const { 
    user, 
    isLoading: authLoading, 
    isFluxosAdmin,
    organizations 
  } = useAuthHook();

  // Filter out test organizations (integration tests)
  const filteredOrganizations = organizations.filter((org) => {
    const orgName = org.name?.toLowerCase() || '';
    return !orgName.includes('test') && !orgName.includes('integration');
  });

  // Auto-redirect: If user has exactly 1 org and is NOT admin, go directly to their org
  useEffect(() => {
    if (!authLoading && user && !isFluxosAdmin && filteredOrganizations.length === 1) {
      router.replace(`/conversations/${filteredOrganizations[0].id}`);
    }
  }, [authLoading, user, isFluxosAdmin, filteredOrganizations, router]);

  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingIndicator text="Loading..." />
      </div>
    );
  }

  if (!user) {
    router.push('/login');
    return null;
  }

  // Show loading while auto-redirecting (non-admin with 1 org)
  if (!isFluxosAdmin && filteredOrganizations.length === 1) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingIndicator text="Loading conversations..." />
      </div>
    );
  }

  // Access control: Check if user has access to any organizations
  if (!isFluxosAdmin && filteredOrganizations.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <HTFBaseCard variant="error" className="max-w-md">
          <div className="flex flex-col items-center gap-3 text-center">
            <AlertTriangle className="w-12 h-12 text-error" />
            <h2 className="text-2xl font-bold">No Access</h2>
            <p className="text-gray-600">
              You don't have access to any organizations.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Contact your administrator to request access to an organization.
            </p>
          </div>
        </HTFBaseCard>
      </div>
    );
  }

  // Only show org selector for admins or users with multiple orgs
  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto py-10 px-6">
        <HTFSectionHeader
          title="Select Organization"
          subtitle={`${filteredOrganizations.length} organization${filteredOrganizations.length === 1 ? '' : 's'} available`}
          icon={Building2}
          className="mb-8"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOrganizations.map((org) => (
            <HTFBaseCard
              key={org.id}
              variant="primary"
              icon={Building2}
              title={org.name}
              className="!border-0"
              onClick={() => {
                console.log('[OrganizationsListPage] Clicked org:', org.id);
                router.push(`/conversations/${org.id}`);
              }}
            >
              {org.brands && org.brands.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-gray-600 font-medium uppercase tracking-wide">
                    Brands
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {org.brands.slice(0, 3).map((brand) => (
                      <span
                        key={brand.id}
                        className="text-xs px-2 py-1 bg-teal-100 text-teal-800 rounded-md"
                      >
                        {brand.name}
                      </span>
                    ))}
                    {org.brands.length > 3 && (
                      <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded-md">
                        +{org.brands.length - 3} more
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div className="mt-4 flex items-center justify-end">
                <ArrowRight className="w-5 h-5 text-gray-600" />
              </div>
            </HTFBaseCard>
          ))}
        </div>
      </div>
    </div>
  );
}

